// components/MediosDePago.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import api from "../helpers/api";

const TIPOS = ["EFECTIVO", "TRANSFERENCIA", "CHEQUE", "ICHEQ"];

const canonTipo = (t) => {
  const s = String(t || "").toLowerCase();
  if (s === "efectivo") return "EFECTIVO";
  if (s === "transferencia") return "TRANSFERENCIA";
  if (s === "cheque" || s === "cheque_tercero" || s === "cheque_propio") return "CHEQUE";
  if (s === "echeq" || s === "icheq") return "ICHEQ";
  return "EFECTIVO";
};

const defaultMedio = (pref = {}) => ({
  _id: Math.random().toString(36).slice(2),
  tipo: "EFECTIVO",
  importe: 0,
  fecha: new Date().toISOString().slice(0, 10),
  fechaValor: "",
  caja: pref.ultimaCaja || "",
  cuentaBancaria: pref.ultimaCuenta || "",
  refTransferencia: "",
  referencia: "",
  numero: "",
  nroCheque: "",
  bancoEmisor: "",
  librador: "",
  cuitLibrador: "",
  fechaPagoDiferido: "",
  idICheq: "",
  emisor: "",
  fechaLiquidacion: "",
  carteraItem: null,
});

export default function MediosDePago({ medios, setMedios, onTotalsChange, onValidityChange }) {
  const [cajas, setCajas] = useState([]);
  const [cuentas, setCuentas] = useState([]);
  const [loadingCajas, setLoadingCajas] = useState(false);
  const [loadingCtas, setLoadingCtas] = useState(false);

  const prefs = useMemo(
    () => ({
      ultimaCaja: localStorage.getItem("prefs.ultimaCaja") || "",
      ultimaCuenta: localStorage.getItem("prefs.ultimaCuenta") || "",
    }),
    []
  );

  /* =======================
     Fetchers con diagnóstico
  ======================= */
  const fetchCajas = useCallback(async () => {
    setLoadingCajas(true);
    try {
      const { data } = await api.get("/cajas", { params: { activas: 1 } });
      console.log("[CAJAS][FRONT] payload:", data);
      const list = data?.cajas ?? (Array.isArray(data) ? data : []);
      const sane = (list || []).filter((x) => x?.activo !== false && x?._id);
      setCajas(sane);
      if (!sane.length) console.warn("[CAJAS][FRONT] lista vacía");
    } catch (e) {
      console.error("[CAJAS][FRONT] ERROR:", e?.response?.status, e?.response?.data || e.message);
      setCajas([]);
      alert("No pude cargar CAJAS. Revisá token / servidor. Mirá la consola para más detalle.");
    } finally {
      setLoadingCajas(false);
    }
  }, []);

  const fetchCuentas = useCallback(async () => {
    setLoadingCtas(true);
    try {
      const { data } = await api.get("/cuentasbancarias", { params: { activas: 1 } });
      console.log("[CUENTAS][FRONT] payload crudo:", data);

      let list = [];
      if (Array.isArray(data)) list = data;
      else if (Array.isArray(data?.cuentas)) list = data.cuentas;
      else if (Array.isArray(data?.cuentasbancarias)) list = data.cuentasbancarias;
      else if (Array.isArray(data?.items)) list = data.items;

      if (data && data.ok === false) {
        throw new Error(data.error || data.msg || "Respuesta ok:false");
      }

      const sane = (list || []).filter((x) => {
        const tieneId = !!x?._id;
        const flagActiva =
          x?.activa !== undefined ? x.activa :
          x?.activo !== undefined ? x.activo :
          true;
        return tieneId && flagActiva;
      });

      console.log("[CUENTAS] cant:", sane.length, sane.map(c => ({
        _id: c._id, alias: c.alias, banco: c.banco, numero: c.numero, activa: c.activa, activo: c.activo
      })));

      setCuentas(sane);

      if (!sane.length) {
        console.warn("[CUENTAS] vacío con ?activas=1 → pruebo sin filtro para diagnosticar");
        try {
          const { data: dataAll } = await api.get("/cuentasbancarias");
          const all = Array.isArray(dataAll)
            ? dataAll
            : dataAll?.cuentas || dataAll?.cuentasbancarias || dataAll?.items || [];
          console.log("[CUENTAS][ALL] total sin filtro:", all.length, all);
        } catch (e2) {
          console.warn("[CUENTAS][ALL] no pude traer sin filtro:", e2?.message || e2);
        }
      }
    } catch (e) {
      console.error("[CUENTAS][FRONT] ERROR:", e?.response?.status, e?.response?.data || e.message);
      setCuentas([]);
      alert(`No pude cargar CUENTAS BANCARIAS. Mirá consola/Network.`);
    } finally {
      setLoadingCtas(false);
    }
  }, []);

  useEffect(() => {
    fetchCajas();
    fetchCuentas();
  }, [fetchCajas, fetchCuentas]);

  /* =======================
     Totales + prefs
  ======================= */
  const totalMedios = useMemo(
    () => (medios || []).reduce((a, m) => a + (Number(m.importe) || 0), 0),
    [medios]
  );
  useEffect(() => onTotalsChange?.(totalMedios), [totalMedios, onTotalsChange]);

  useEffect(() => {
    const lastCash = [...(medios || [])].reverse().find((m) => canonTipo(m.tipo) === "EFECTIVO" && m.caja);
    if (lastCash) localStorage.setItem("prefs.ultimaCaja", lastCash.caja);
    const lastAcc = [...(medios || [])].reverse().find(
      (m) => canonTipo(m.tipo) === "TRANSFERENCIA" && m.cuentaBancaria
    );
    if (lastAcc) localStorage.setItem("prefs.ultimaCuenta", lastAcc.cuentaBancaria);
  }, [medios]);

  /* =======================
     Validación
  ======================= */
  const rowErrors = useMemo(
    () =>
      (medios || []).map((m) => {
        const errs = {};
        const tipo = canonTipo(m.tipo);
        if (!(Number(m.importe) > 0)) errs.importe = "Importe > 0";
        if (tipo === "EFECTIVO" && !m.caja) errs.caja = "Seleccione caja";
        if (tipo === "TRANSFERENCIA" && !m.cuentaBancaria) errs.cuentaBancaria = "Seleccione cuenta";
        if (tipo === "CHEQUE" && !m.nroCheque) errs.nroCheque = "N° de cheque requerido";
        if (tipo === "ICHEQ" && !m.idICheq) errs.idICheq = "ID iCheq requerido";
        return errs;
      }),
    [medios]
  );

  const isValid = useMemo(
    () => (medios?.length || 0) > 0 && rowErrors.every((e) => Object.keys(e).length === 0),
    [medios, rowErrors]
  );
  useEffect(() => onValidityChange?.({ valid: isValid, rowErrors }), [isValid, rowErrors, onValidityChange]);

  /* =======================
     Edición
  ======================= */
  const addRow = () => setMedios([...(medios || []), defaultMedio(prefs)]);
  const delRow = (idx) => setMedios((arr) => arr.filter((_, i) => i !== idx));
  const setRow = (idx, patch) =>
    setMedios((arr) => {
      const copy = [...arr];
      copy[idx] = { ...copy[idx], ...patch };
      return copy;
    });

  const onChangeTipo = (idx, tipo) => {
    const canon = canonTipo(tipo);
    const reset = {
      caja: "",
      cuentaBancaria: "",
      refTransferencia: "",
      referencia: "",
      numero: "",
      fechaValor: "",
      nroCheque: "",
      bancoEmisor: "",
      librador: "",
      cuitLibrador: "",
      fechaPagoDiferido: "",
      idICheq: "",
      emisor: "",
      fechaLiquidacion: "",
    };
    setRow(idx, { tipo: canon, ...reset });
  };

  return (
    <div className="medios-wrapper">
      {/* estilos locales */}
      <style>{`
        .mp-row{
          border:1px solid #ddd; border-radius:8px; padding:8px; margin-bottom:8px;
          align-items:center;
        }
        .mp-label{ text-align:right; color:#666; }
        .mp-xs{ width:110px; }
        .mp-date{ width:132px; }
        .mp-del{
          width:28px; height:28px; line-height:26px; text-align:center;
          border:1px solid #dc3545; color:#dc3545; background:#fff; border-radius:6px;
          cursor:pointer; font-weight:700; padding:0;
        }
        .mp-del:hover{ background:#dc354511; }
        .mp-bad{ border:1px solid crimson !important; }
        .mp-err{ color:crimson; font-size:12px; white-space:nowrap; }
        .mp-add{ margin-bottom:6px; display:flex; align-items:center; gap:8px; }
        .mp-help{ color:#888; font-size:12px; margin-top:4px; }
      `}</style>

      <div className="mp-add">
        <h3 style={{margin:0}}>Medios de pago</h3>
        <button type="button" onClick={addRow}>+ Agregar medio</button>
        <span style={{flex:1}} />
        <button type="button" onClick={fetchCajas} title="Recargar cajas">Recargar cajas</button>
        <button type="button" onClick={fetchCuentas} title="Recargar cuentas">Recargar cuentas</button>
      </div>

      {(medios || []).map((m, idx) => {
        const e = rowErrors[idx] || {};
        const tipo = canonTipo(m.tipo);

        const badCaja = tipo === "EFECTIVO" && e.caja;
        const badCta  = tipo === "TRANSFERENCIA" && e.cuentaBancaria;
        const badImp  = !!e.importe;

        if (tipo === "EFECTIVO") {
          return (
            <div
              key={m._id}
              className="mp-row"
              style={{ display: "grid", gridTemplateColumns: "120px 110px 110px 40px minmax(180px,1fr) 36px", columnGap: 8 }}
            >
              <select value={m.tipo} onChange={(ev) => onChangeTipo(idx, ev.target.value)}>
                {TIPOS.map((t) => (<option key={t} value={t}>{t}</option>))}
              </select>

              <div style={{ display: "grid", gridTemplateColumns: "1fr auto", alignItems: "center", columnGap: 6 }}>
                <input
                  className={`mp-xs ${badImp ? "mp-bad" : ""}`}
                  type="number"
                  step="0.01"
                  min="0"
                  value={m.importe}
                  onChange={(ev) => setRow(idx, { importe: parseFloat(ev.target.value) || 0 })}
                />
                {badImp ? <span className="mp-err">Importe &gt; 0</span> : <span />}
              </div>

              <input
                className="mp-date"
                type="date"
                value={(m.fecha || "").slice(0, 10)}
                onChange={(ev) => setRow(idx, { fecha: ev.target.value })}
              />

              <span className="mp-label">Caja</span>

              <div>
                <select
                  className={badCaja ? "mp-bad" : ""}
                  value={m.caja}
                  disabled={loadingCajas}
                  onChange={(ev) => setRow(idx, { caja: ev.target.value })}
                >
                  <option value="">{loadingCajas ? "Cargando..." : "-- Seleccione caja --"}</option>
                  {cajas.map((c) => (
                    <option key={c._id} value={c._id}>
                      {c.descripcion || c.nombre || `Caja ${c.codcaj || ""}`}
                    </option>
                  ))}
                </select>
                {!loadingCajas && !cajas.length && (
                  <div className="mp-help">No hay cajas activas.</div>
                )}
              </div>

              <button type="button" className="mp-del" title="Quitar" onClick={() => delRow(idx)}>
                ×
              </button>
            </div>
          );
        }

        if (tipo === "TRANSFERENCIA") {
          return (
            <div
              key={m._id}
              className="mp-row"
              style={{
                display: "grid",
                gridTemplateColumns:
                  "120px 110px 100px 60px minmax(120px,1fr) 40px minmax(160px,1fr) 65px 130px 36px",
                columnGap: 8,
              }}
            >
              <select value={m.tipo} onChange={(ev) => onChangeTipo(idx, ev.target.value)}>
                {TIPOS.map((t) => (<option key={t} value={t}>{t}</option>))}
              </select>

              <div style={{ display: "grid", gridTemplateColumns: "1fr auto", alignItems: "center", columnGap: 6 }}>
                <input
                  className={`mp-xs ${badImp ? "mp-bad" : ""}`}
                  type="number"
                  step="0.01"
                  min="0"
                  value={m.importe}
                  onChange={(ev) => setRow(idx, { importe: parseFloat(ev.target.value) || 0 })}
                />
                {badImp ? <span className="mp-err">Importe &gt; 0</span> : <span />}
              </div>

              <input
                className="mp-date"
                type="date"
                value={(m.fecha || "").slice(0, 10)}
                onChange={(ev) => setRow(idx, { fecha: ev.target.value })}
              />

              <span className="mp-label">Cuenta</span>

              <div>
                <select
                  className={badCta ? "mp-bad" : ""}
                  value={m.cuentaBancaria}
                  disabled={loadingCtas}
                  onChange={(ev) => setRow(idx, { cuentaBancaria: ev.target.value })}
                >
                  <option value="">{loadingCtas ? "Cargando..." : "-- Seleccione cuenta --"}</option>
                  {cuentas.map((cta) => (
                    <option key={cta._id} value={cta._id}>
                      {cta.alias || `${cta.banco}${cta.numero ? ` - ${cta.numero}` : ""}`}
                    </option>
                  ))}
                </select>
                {!loadingCtas && !cuentas.length && (
                  <div className="mp-help">
                    No hay cuentas activas o no cargaron. Usá “Recargar cuentas”. Si sigue vacío, revisá token o
                    `GET /cuentasbancarias` en Network.
                  </div>
                )}
              </div>

              <span className="mp-label">Ref</span>
              <input
                value={m.refTransferencia || m.referencia || m.numero || ""}
                onChange={(ev) =>
                  setRow(idx, { refTransferencia: ev.target.value, numero: ev.target.value })
                }
                placeholder="N° operación"
              />

              <span className="mp-label">F. valor</span>
              <input
                className="mp-date"
                type="date"
                value={(m.fechaValor || "").slice(0, 10)}
                onChange={(ev) => setRow(idx, { fechaValor: ev.target.value })}
              />

              <button type="button" className="mp-del" title="Quitar" onClick={() => delRow(idx)}>
                ×
              </button>
            </div>
          );
        }

        if (tipo === "CHEQUE") {
          const badNro = !!e.nroCheque;
          return (
            <div
              key={m._id}
              className="mp-row"
              style={{
                display: "grid",
                gridTemplateColumns:
                  "120px 110px 110px 30px minmax(60px,1fr) 40px minmax(80px,1fr) 40px minmax(60px,1fr) 50px 102px 36px",
                columnGap: 8,
              }}
            >
              <select value={m.tipo} onChange={(ev) => onChangeTipo(idx, ev.target.value)}>
                {TIPOS.map((t) => (<option key={t} value={t}>{t}</option>))}
              </select>

              <div style={{ display: "grid", gridTemplateColumns: "1fr auto", alignItems: "center", columnGap: 6 }}>
                <input
                  className={`mp-xs ${badImp ? "mp-bad" : ""}`}
                  type="number"
                  step="0.01"
                  min="0"
                  value={m.importe}
                  onChange={(ev) => setRow(idx, { importe: parseFloat(ev.target.value) || 0 })}
                />
                {badImp ? <span className="mp-err">Importe &gt; 0</span> : <span />}
              </div>

              <input
                className="mp-date"
                type="date"
                value={(m.fecha || "").slice(0, 10)}
                onChange={(ev) => setRow(idx, { fecha: ev.target.value })}
              />

              <span className="mp-label">N°</span>
              <div>
                <input
                  className={badNro ? "mp-bad" : ""}
                  value={m.nroCheque || ""}
                  onChange={(ev) => setRow(idx, { nroCheque: ev.target.value, numero: ev.target.value })}
                />
                {badNro ? <div className="mp-err">N° de cheque requerido</div> : null}
              </div>

              <span className="mp-label">Banco</span>
              <input value={m.bancoEmisor || ""} onChange={(ev) => setRow(idx, { bancoEmisor: ev.target.value })} />

              <span className="mp-label">Librador</span>
              <input value={m.librador || ""} onChange={(ev) => setRow(idx, { librador: ev.target.value })} />

              <span className="mp-label">CUIT</span>
              <input value={m.cuitLibrador || ""} onChange={(ev) => setRow(idx, { cuitLibrador: ev.target.value })} />

              <span className="mp-label">F. difer.</span>
              <input
                className="mp-date"
                type="date"
                value={(m.fechaPagoDiferido || "").slice(0, 10)}
                onChange={(ev) => setRow(idx, { fechaPagoDiferido: ev.target.value })}
              />

              <button type="button" className="mp-del" title="Quitar" onClick={() => delRow(idx)}>
                ×
              </button>
            </div>
          );
        }

        // ICHEQ
        const badID = !!e.idICheq;
        return (
          <div
            key={m._id}
            className="mp-row"
            style={{
              display: "grid",
              gridTemplateColumns:
                "120px 110px 100px 40px minmax(180px,1fr) 60px minmax(180px,1fr) 70px 132px 36px",
              columnGap: 8,
            }}
          >
            <select value={m.tipo} onChange={(ev) => onChangeTipo(idx, ev.target.value)}>
              {TIPOS.map((t) => (<option key={t} value={t}>{t}</option>))}
            </select>

            <div style={{ display: "grid", gridTemplateColumns: "1fr auto", alignItems: "center", columnGap: 6 }}>
              <input
                className={`mp-xs ${badImp ? "mp-bad" : ""}`}
                type="number"
                step="0.01"
                min="0"
                value={m.importe}
                onChange={(ev) => setRow(idx, { importe: parseFloat(ev.target.value) || 0 })}
              />
              {badImp ? <span className="mp-err">Importe &gt; 0</span> : <span />}
            </div>

            <input
              className="mp-date"
              type="date"
              value={(m.fecha || "").slice(0, 10)}
              onChange={(ev) => setRow(idx, { fecha: ev.target.value })}
            />

            <span className="mp-label">ID</span>
            <div>
              <input
                className={badID ? "mp-bad" : ""}
                value={m.idICheq || ""}
                onChange={(ev) => setRow(idx, { idICheq: ev.target.value, numero: ev.target.value })}
              />
              {badID ? <div className="mp-err">ID iCheq requerido</div> : null}
            </div>

            <span className="mp-label">Emisor</span>
            <input value={m.emisor || ""} onChange={(ev) => setRow(idx, { emisor: ev.target.value })} />

            <span className="mp-label">F. liqui.</span>
            <input
              className="mp-date"
              type="date"
              value={(m.fechaLiquidacion || "").slice(0, 10)}
              onChange={(ev) => setRow(idx, { fechaLiquidacion: ev.target.value })}
            />

            <button type="button" className="mp-del" title="Quitar" onClick={() => delRow(idx)}>
              ×
            </button>
          </div>
        );
      })}

      <div style={{ textAlign: "right", marginTop: 8 }}>
        <strong>Total medios: ${totalMedios.toFixed(2)}</strong>
      </div>
    </div>
  );
}
